// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setDropShipModalIsOpen from "@guestsupply/App/Handlers/AddToListModal/SetDropShipModalIsOpen";
import xDropShipContactInfo from "@guestsupply/App/Handlers/XNG/DropShipOrdersModal";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { xValidateOnSubmit } from "@guestsupply/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: DropShipOrdersModal;
}

interface DropShipOrdersData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState) => ({
    modalIsOpen: state.xDropShipModal.isOpen,
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    setDropShipModalIsOpen,
    xDropShipContactInfo,
};

export interface DropShipOrdersModal {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    description?: TypographyProps;
    buttonsWrapper?: InjectableCss;
    updateButton?: ButtonPresentationProps;
}

export const dropShipOrdersModal: DropShipOrdersModal = {};

const StyledForm = getStyledWrapper("form");
const formId = "dropShipOrdersForm";

const DropShipOrdersModal = ({
    modalIsOpen,
    cart,
    setDropShipModalIsOpen,
    xDropShipContactInfo,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(dropShipOrdersModal, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const [dropShipData, setDropShipData] = useState<DropShipOrdersData>();
    const [isLoading, setIsLoading] = useState(false);

    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setDropShipData(prev => {
            const current = prev ?? {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
            };

            return {
                ...current,
                [name]: value,
            };
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const errors = xValidateOnSubmit(formId);

        if (errors.length) {
            return;
        }

        setIsLoading(true);

        xDropShipContactInfo({
            dropShipContactInfo: JSON.stringify(dropShipData),
            onSuccess: () => {
                setDropShipModalIsOpen({ modalIsOpen: false });
                toasterContext.addToast({
                    body: translate("Custom_DropShipOrdersModal_SuccessMessage"),
                    messageType: "success",
                });
                setDropShipData({} as DropShipOrdersData);
            },
            onComplete: () => setIsLoading(false),
        });
    };

    useEffect(() => {
        if (cart?.properties?.dropShipContactInfo) {
            const dropShipContactInfo = JSON.parse(cart.properties.dropShipContactInfo);
            setDropShipData(dropShipContactInfo);
        }
    }, [cart]);

    return (
        <Modal {...styles.modal} headline={translate("Drop Ship Orders")} isOpen={modalIsOpen} isCloseable={false}>
            <LoadingOverlay loading={isLoading}>
                <StyledForm onSubmit={handleSubmit} noValidate>
                    <StyledWrapper {...styles.innerWrapper} data-test-selector="Drop_Ship_Orders_Modal_Wrapper">
                        <Typography {...styles.description} data-test-selector="Drop_Ship_Orders_Modal_Description">
                            {siteMessage("Custom_DropShipOrdersModal_Description")}
                        </Typography>
                        <TextField
                            name="firstName"
                            label={translate("First Name")}
                            placeholder={translate("First Name")}
                            value={dropShipData?.firstName}
                            onChange={handleChangeInput}
                            type="firstName"
                            required
                            data-test-selector="Drop_Ship_Orders_Modal_FirstName"
                            {...{ formId }}
                        />
                        <TextField
                            name="lastName"
                            label={translate("Last Name")}
                            placeholder={translate("Last Name")}
                            value={dropShipData?.lastName}
                            onChange={handleChangeInput}
                            type="lastName"
                            required
                            data-test-selector="Drop_Ship_Orders_Modal_LastName"
                            {...{ formId }}
                        />
                        <TextField
                            name="phone"
                            label={translate("Phone Number")}
                            placeholder={translate("Phone Number")}
                            value={dropShipData?.phone}
                            onChange={handleChangeInput}
                            type="altPhone"
                            required
                            data-test-selector="Drop_Ship_Orders_Modal_PhoneNumber"
                            {...{ formId }}
                        />
                        <TextField
                            name="email"
                            label={translate("Email")}
                            placeholder={translate("Email")}
                            value={dropShipData?.email}
                            onChange={handleChangeInput}
                            type="email"
                            required
                            data-test-selector="Drop_Ship_Orders_Modal_Email"
                            {...{
                                formId,
                                validationRules: "required|min:10|max:125|email",
                            }}
                        />
                        <StyledWrapper {...styles.buttonsWrapper}>
                            <Button
                                {...styles.updateButton}
                                type="submit"
                                data-test-selector="Drop_Ship_Orders_Modal_UpdateButton"
                            >
                                {translate("Update")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledForm>
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DropShipOrdersModal);
